import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/components/AuthProvider";
import { supabase } from '@/integrations/supabase/client';

// Input validation helper
const validateInput = (value: string, fieldName: string, minLength = 1, maxLength = 255): string | null => {
  if (!value || value.trim().length < minLength) {
    return `${fieldName} must be at least ${minLength} characters long`;
  }
  if (value.trim().length > maxLength) {
    return `${fieldName} must be no more than ${maxLength} characters long`;
  }
  // Basic XSS prevention
  if (/<script|javascript:|on\w+\s*=|<iframe|<object|<embed/i.test(value)) {
    return `${fieldName} contains invalid characters`;
  }
  return null;
};

const NewProject = () => {
  const [formData, setFormData] = useState({
    projectName: '',
    developer: '',
    methodology: '',
    location: '',
    description: '',
    projectType: '',
    vintageYear: '',
    creditsIssued: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();
  const { user } = useAuth();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    // Validate all required fields
    const validationRules = [
      { field: 'projectName', value: formData.projectName, label: 'Project Name' },
      { field: 'developer', value: formData.developer, label: 'Developer' },
      { field: 'methodology', value: formData.methodology, label: 'Methodology' },
      { field: 'location', value: formData.location, label: 'Location' },
      { field: 'description', value: formData.description, label: 'Description', maxLength: 1000 },
      { field: 'projectType', value: formData.projectType, label: 'Project Type' }
    ];

    validationRules.forEach(rule => {
      const error = validateInput(rule.value, rule.label, 1, rule.maxLength || 255);
      if (error) errors[rule.field] = error;
    });

    // Validate vintage year
    const vintageYear = parseInt(formData.vintageYear);
    const currentYear = new Date().getFullYear();
    if (!formData.vintageYear || isNaN(vintageYear) || vintageYear < 1990 || vintageYear > currentYear) {
      errors.vintageYear = `Vintage year must be between 1990 and ${currentYear}`;
    }

    // Validate credits issued
    const creditsIssued = parseInt(formData.creditsIssued);
    if (!formData.creditsIssued || isNaN(creditsIssued) || creditsIssued < 1 || creditsIssued > 10000000) {
      errors.creditsIssued = 'Credits issued must be between 1 and 10,000,000';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to register a project.",
        variant: "destructive"
      });
      return;
    }

    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please correct the errors in the form.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('Starting project registration...');
      
      // Generate a unique registry ID
      const registryId = `ICX-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      const projectData = {
        name: formData.projectName.trim(),
        developer: formData.developer.trim(),
        methodology: formData.methodology.trim(),
        location: formData.location.trim(),
        description: formData.description.trim(),
        type: formData.projectType,
        vintage_year: parseInt(formData.vintageYear),
        credits_issued: parseInt(formData.creditsIssued),
        registry_id: registryId,
        status: 'active' as const,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        user_id: user.id
      };

      console.log('Inserting project data:', projectData);
      
      // Insert project into database
      const { data: project, error: dbError } = await supabase
        .from('carbon_projects')
        .insert(projectData)
        .select('*')
        .single();

      if (dbError) {
        console.error('Database error:', dbError);
        throw new Error(`Database error: ${dbError.message}`);
      }

      if (!project) {
        throw new Error('Failed to create project');
      }

      console.log('Project inserted successfully:', project);

      toast({
        title: "Project Registered",
        description: "Your project has been successfully registered.",
      });

      // Reset form
      setFormData({
        projectName: '',
        developer: '',
        methodology: '',
        location: '',
        description: '',
        projectType: '',
        vintageYear: '',
        creditsIssued: ''
      });

    } catch (error) {
      console.error('Project registration error:', error);
      setError(error instanceof Error ? error.message : 'An unexpected error occurred');
      toast({
        title: "Registration Failed",
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto mt-8 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle>Register New Carbon Credit Project</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="projectName">Project Name</Label>
              <Input
                type="text"
                id="projectName"
                name="projectName"
                value={formData.projectName}
                onChange={handleChange}
                placeholder="Enter project name"
                required
                maxLength={255}
              />
              {validationErrors.projectName && (
                <p className="text-sm text-red-600 mt-1">{validationErrors.projectName}</p>
              )}
            </div>

            <div>
              <Label htmlFor="developer">Developer</Label>
              <Input
                type="text"
                id="developer"
                name="developer"
                value={formData.developer}
                onChange={handleChange}
                placeholder="Enter developer name"
                required
                maxLength={255}
              />
              {validationErrors.developer && (
                <p className="text-sm text-red-600 mt-1">{validationErrors.developer}</p>
              )}
            </div>

            <div>
              <Label htmlFor="methodology">Methodology</Label>
              <Input
                type="text"
                id="methodology"
                name="methodology"
                value={formData.methodology}
                onChange={handleChange}
                placeholder="Enter methodology"
                required
                maxLength={255}
              />
              {validationErrors.methodology && (
                <p className="text-sm text-red-600 mt-1">{validationErrors.methodology}</p>
              )}
            </div>

            <div>
              <Label htmlFor="location">Location</Label>
              <Input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="Enter location"
                required
                maxLength={255}
              />
              {validationErrors.location && (
                <p className="text-sm text-red-600 mt-1">{validationErrors.location}</p>
              )}
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter description"
                required
                maxLength={1000}
                rows={3}
              />
              {validationErrors.description && (
                <p className="text-sm text-red-600 mt-1">{validationErrors.description}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {formData.description.length}/1000 characters
              </p>
            </div>

            <div>
              <Label htmlFor="projectType">Project Type</Label>
              <Select value={formData.projectType} onValueChange={(value) => setFormData(prev => ({ ...prev, projectType: value }))}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Renewable Energy">Renewable Energy</SelectItem>
                  <SelectItem value="Forestry">Forestry</SelectItem>
                  <SelectItem value="Waste Management">Waste Management</SelectItem>
                  <SelectItem value="Agriculture">Agriculture</SelectItem>
                  <SelectItem value="Transportation">Transportation</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
              {validationErrors.projectType && (
                <p className="text-sm text-red-600 mt-1">{validationErrors.projectType}</p>
              )}
            </div>

            <div>
              <Label htmlFor="vintageYear">Vintage Year</Label>
              <Input
                type="number"
                id="vintageYear"
                name="vintageYear"
                value={formData.vintageYear}
                onChange={handleChange}
                placeholder="Enter vintage year"
                min="1990"
                max={new Date().getFullYear()}
                required
              />
              {validationErrors.vintageYear && (
                <p className="text-sm text-red-600 mt-1">{validationErrors.vintageYear}</p>
              )}
            </div>

            <div>
              <Label htmlFor="creditsIssued">Credits Issued</Label>
              <Input
                type="number"
                id="creditsIssued"
                name="creditsIssued"
                value={formData.creditsIssued}
                onChange={handleChange}
                placeholder="Enter credits issued"
                min="1"
                max="10000000"
                required
              />
              {validationErrors.creditsIssued && (
                <p className="text-sm text-red-600 mt-1">{validationErrors.creditsIssued}</p>
              )}
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            <Button 
              type="submit" 
              disabled={loading || !user} 
              className="w-full"
            >
              {loading ? "Registering..." : "Register Project"}
            </Button>

            {!user && (
              <p className="text-sm text-gray-600 text-center">
                Please sign in to register a project.
              </p>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default NewProject;